import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex gap-3"
}
const _hoisted_2 = ["href", "aria-label"]
const _hoisted_3 = ["src"]
const _hoisted_4 = {
  key: 1,
  class: "rounded-full h-10 w-10 bg-gray-300 dark:bg-gray-700 flex items-center justify-center text-sm",
  "aria-hidden": "true"
}
const _hoisted_5 = { class: "flex-1 min-w-0" }
const _hoisted_6 = { class: "flex items-baseline gap-2 flex-wrap" }
const _hoisted_7 = ["href"]
const _hoisted_8 = {
  key: 0,
  class: "text-muted text-sm truncate"
}
const _hoisted_9 = ["href"]
const _hoisted_10 = { class: "mt-1 whitespace-pre-wrap break-words" }
const _hoisted_11 = ["href"]
const _hoisted_12 = {
  key: 0,
  class: "mt-2 flex gap-2 flex-wrap"
}
const _hoisted_13 = ["href"]
const _hoisted_14 = ["src", "alt"]
const _hoisted_15 = ["href"]
const _hoisted_16 = { class: "flex" }
const _hoisted_17 = ["src", "alt"]
const _hoisted_18 = { class: "p-3 min-w-0" }
const _hoisted_19 = { class: "text-sm font-medium truncate" }
const _hoisted_20 = { class: "text-xs text-muted line-clamp-2 mt-1" }
const _hoisted_21 = {
  key: 2,
  class: "flex items-center gap-4 mt-2 text-sm text-muted"
}
const _hoisted_22 = { key: 0 }
const _hoisted_23 = { key: 1 }
const _hoisted_24 = {
  key: 3,
  class: "mt-4 flex flex-col gap-4 border-l border-gray-300 dark:border-gray-800"
}
const _hoisted_25 = {
  key: 1,
  class: "pt-3 pl-1"
}
const _hoisted_26 = { class: "flex items-center gap-2 flex-wrap" }
const _hoisted_27 = ["href", "aria-label"]
const _hoisted_28 = ["src"]
const _hoisted_29 = {
  key: 1,
  class: "rounded-full h-6 w-6 bg-gray-300 dark:bg-gray-700 flex items-center justify-center text-xs",
  "aria-hidden": "true"
}
const _hoisted_30 = ["href"]
const _hoisted_31 = {
  key: 0,
  class: "text-muted text-sm truncate"
}
const _hoisted_32 = ["href"]
const _hoisted_33 = { class: "mt-1 whitespace-pre-wrap break-words" }
const _hoisted_34 = ["href"]
const _hoisted_35 = {
  key: 0,
  class: "mt-2 flex gap-2 flex-wrap"
}
const _hoisted_36 = ["href"]
const _hoisted_37 = ["src", "alt"]
const _hoisted_38 = ["href"]
const _hoisted_39 = { class: "flex" }
const _hoisted_40 = ["src", "alt"]
const _hoisted_41 = { class: "p-2 min-w-0" }
const _hoisted_42 = { class: "text-sm font-medium truncate" }
const _hoisted_43 = { class: "text-xs text-muted line-clamp-2 mt-1" }
const _hoisted_44 = {
  key: 0,
  class: "flex flex-col border-l border-gray-300 dark:border-gray-800"
}
const _hoisted_45 = ["href"]

import type {
  AppBskyEmbedImages,
  AppBskyEmbedExternal,
} from '@atproto/api'
import type { Facet, BlueskyFacetFeature } from '#shared/utils/bluesky'
import { segmentize, atUriToWebUrl } from '#shared/utils/bluesky'

interface CommentEmbed {
  type: 'images' | 'external'
  images?: AppBskyEmbedImages.ViewImage[]
  external?: AppBskyEmbedExternal.ViewExternal
}

interface Comment {
  uri: string
  cid: string
  author: {
    did: string
    handle: string
    displayName?: string
    avatar?: string
  }
  text: string
  facets?: Facet<BlueskyFacetFeature>[]
  embed?: CommentEmbed
  createdAt: string
  likeCount: number
  replyCount: number
  repostCount: number
  replies: Comment[]
}

const maxDepth = 4

export default /*@__PURE__*/_defineComponent({
  __name: 'BlueskyComment',
  props: {
    comment: {},
    depth: {}
  },
  setup(__props: any) {

function getCommentUrl (comment: Comment): string {
  return atUriToWebUrl(comment.uri) ?? '#'
}

function getFeatureUrl (feature: BlueskyFacetFeature): string | undefined {
  if (feature.$type === 'app.bsky.richtext.facet#link') return feature.uri
  if (feature.$type === 'app.bsky.richtext.facet#mention') return `https://bsky.app/profile/${feature.did}`
  if (feature.$type === 'app.bsky.richtext.facet#tag') return `https://bsky.app/hashtag/${feature.tag}`
  return undefined
}

const props = __props

const commentUrl = computed(() => getCommentUrl(props.comment))
const segments = computed(() => segmentize(props.comment.text, props.comment.facets))

return (_ctx: any,_cache: any) => {
  const _component_NuxtTime = _resolveComponent("NuxtTime")!
  const _component_BlueskyComment = _resolveComponent("BlueskyComment", true)!

  return (__props.depth === 0)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("a", {
          href: `https://bsky.app/profile/${__props.comment.author.handle}`,
          target: "_blank",
          rel: "noopener noreferrer",
          class: "flex-shrink-0 rounded-full f-ring",
          "aria-label": `View ${__props.comment.author.displayName || __props.comment.author.handle}'s profile on Bluesky`
        }, [
          (__props.comment.author.avatar)
            ? (_openBlock(), _createElementBlock("img", {
                key: 0,
                src: __props.comment.author.avatar,
                alt: "",
                class: "rounded-full h-10 w-10",
                width: "40",
                height: "40",
                loading: "lazy"
              }, null, 8, _hoisted_3))
            : (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString((__props.comment.author.displayName || __props.comment.author.handle).charAt(0).toUpperCase()), 1))
        ], 8, _hoisted_2),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("a", {
              href: `https://bsky.app/profile/${__props.comment.author.handle}`,
              target: "_blank",
              rel: "noopener",
              class: "font-medium hover:underline truncate rounded f-ring"
            }, _toDisplayString(__props.comment.author.displayName || `@${__props.comment.author.handle}`), 9, _hoisted_7),
            (__props.comment.author.displayName)
              ? (_openBlock(), _createElementBlock("span", _hoisted_8, " @" + _toDisplayString(__props.comment.author.handle), 1))
              : _createCommentVNode("", true),
            _createElementVNode("a", {
              href: _unref(commentUrl),
              target: "_blank",
              rel: "noopener",
              class: "text-muted text-sm hover:underline rounded f-ring"
            }, [
              _createVNode(_component_NuxtTime, {
                relative: "",
                datetime: __props.comment.createdAt
              }, null, 8, ["datetime"])
            ], 8, _hoisted_9)
          ]),
          _createElementVNode("p", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(segments), (segment, i) => {
              return (_openBlock(), _createElementBlock(_Fragment, { key: i }, [
                (segment.features?.[0] && getFeatureUrl(segment.features[0]))
                  ? (_openBlock(), _createElementBlock("a", {
                      key: 0,
                      href: getFeatureUrl(segment.features[0]),
                      target: "_blank",
                      rel: "noopener",
                      class: "text-blue-600 dark:text-blue-400 hover:underline rounded f-ring"
                    }, _toDisplayString(segment.text), 9, _hoisted_11))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createTextVNode(_toDisplayString(segment.text), 1)
                    ], 64))
              ], 64))
            }), 128))
          ]),
          (__props.comment.embed?.type === 'images' && __props.comment.embed.images)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.comment.embed.images, (img, i) => {
                  return (_openBlock(), _createElementBlock("a", {
                    key: i,
                    href: img.fullsize,
                    target: "_blank",
                    rel: "noopener",
                    class: "block rounded-lg f-ring"
                  }, [
                    _createElementVNode("img", {
                      src: img.thumb,
                      alt: img.alt || 'Image',
                      class: "rounded-lg max-h-48 object-cover",
                      loading: "lazy"
                    }, null, 8, _hoisted_14)
                  ], 8, _hoisted_13))
                }), 128))
              ]))
            : _createCommentVNode("", true),
          (__props.comment.embed?.type === 'external' && __props.comment.embed.external)
            ? (_openBlock(), _createElementBlock("a", {
                key: 1,
                href: __props.comment.embed.external.uri,
                target: "_blank",
                rel: "noopener",
                class: "mt-2 block border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden hover:border-gray-400 dark:hover:border-gray-600 transition-colors f-ring-inset"
              }, [
                _createElementVNode("div", _hoisted_16, [
                  (__props.comment.embed.external.thumb)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: __props.comment.embed.external.thumb,
                        alt: __props.comment.embed.external.title,
                        class: "w-24 h-24 object-cover flex-shrink-0",
                        loading: "lazy"
                      }, null, 8, _hoisted_17))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_18, [
                    _createElementVNode("p", _hoisted_19, _toDisplayString(__props.comment.embed.external.title), 1),
                    _createElementVNode("p", _hoisted_20, _toDisplayString(__props.comment.embed.external.description), 1)
                  ])
                ])
              ], 8, _hoisted_15))
            : _createCommentVNode("", true),
          (__props.comment.likeCount > 0 || __props.comment.repostCount > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                (__props.comment.likeCount > 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_22, _toDisplayString(__props.comment.likeCount) + " " + _toDisplayString(__props.comment.likeCount === 1 ? 'like' : 'likes'), 1))
                  : _createCommentVNode("", true),
                (__props.comment.repostCount > 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_23, _toDisplayString(__props.comment.repostCount) + " " + _toDisplayString(__props.comment.repostCount === 1 ? 'repost' : 'reposts'), 1))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (__props.comment.replies.length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.comment.replies, (reply) => {
                  return (_openBlock(), _createBlock(_component_BlueskyComment, {
                    key: reply.uri,
                    comment: reply,
                    depth: __props.depth + 1
                  }, null, 8, ["comment", "depth"]))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ])
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_25, [
        _createElementVNode("div", _hoisted_26, [
          _createElementVNode("a", {
            href: `https://bsky.app/profile/${__props.comment.author.handle}`,
            target: "_blank",
            rel: "noopener noreferrer",
            class: "flex-shrink-0 rounded-full f-ring p-1",
            "aria-label": `View ${__props.comment.author.displayName || __props.comment.author.handle}'s profile on Bluesky`
          }, [
            (__props.comment.author.avatar)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  src: __props.comment.author.avatar,
                  alt: "",
                  class: "rounded-full h-6 w-6",
                  width: "24",
                  height: "24",
                  loading: "lazy"
                }, null, 8, _hoisted_28))
              : (_openBlock(), _createElementBlock("div", _hoisted_29, _toDisplayString((__props.comment.author.displayName || __props.comment.author.handle).charAt(0).toUpperCase()), 1))
          ], 8, _hoisted_27),
          _createElementVNode("a", {
            href: `https://bsky.app/profile/${__props.comment.author.handle}`,
            target: "_blank",
            rel: "noopener",
            class: "font-medium hover:underline truncate rounded f-ring"
          }, _toDisplayString(__props.comment.author.displayName || `@${__props.comment.author.handle}`), 9, _hoisted_30),
          (__props.comment.author.displayName)
            ? (_openBlock(), _createElementBlock("span", _hoisted_31, " @" + _toDisplayString(__props.comment.author.handle), 1))
            : _createCommentVNode("", true),
          _createElementVNode("a", {
            href: _unref(commentUrl),
            target: "_blank",
            rel: "noopener",
            class: "text-muted text-sm hover:underline rounded f-ring"
          }, [
            _createVNode(_component_NuxtTime, {
              relative: "",
              datetime: __props.comment.createdAt
            }, null, 8, ["datetime"])
          ], 8, _hoisted_32)
        ]),
        _createElementVNode("p", _hoisted_33, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(segments), (segment, i) => {
            return (_openBlock(), _createElementBlock(_Fragment, { key: i }, [
              (segment.features?.[0] && getFeatureUrl(segment.features[0]))
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    href: getFeatureUrl(segment.features[0]),
                    target: "_blank",
                    rel: "noopener",
                    class: "text-blue-600 dark:text-blue-400 hover:underline rounded f-ring"
                  }, _toDisplayString(segment.text), 9, _hoisted_34))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode(_toDisplayString(segment.text), 1)
                  ], 64))
            ], 64))
          }), 128))
        ]),
        (__props.comment.embed?.type === 'images' && __props.comment.embed.images)
          ? (_openBlock(), _createElementBlock("div", _hoisted_35, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.comment.embed.images, (img, i) => {
                return (_openBlock(), _createElementBlock("a", {
                  key: i,
                  href: img.fullsize,
                  target: "_blank",
                  rel: "noopener",
                  class: "block rounded-lg f-ring"
                }, [
                  _createElementVNode("img", {
                    src: img.thumb,
                    alt: img.alt || 'Image',
                    class: "rounded-lg max-h-32 object-cover",
                    loading: "lazy"
                  }, null, 8, _hoisted_37)
                ], 8, _hoisted_36))
              }), 128))
            ]))
          : _createCommentVNode("", true),
        (__props.comment.embed?.type === 'external' && __props.comment.embed.external)
          ? (_openBlock(), _createElementBlock("a", {
              key: 1,
              href: __props.comment.embed.external.uri,
              target: "_blank",
              rel: "noopener",
              class: "mt-2 block border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden hover:border-gray-400 dark:hover:border-gray-600 transition-colors f-ring-inset"
            }, [
              _createElementVNode("div", _hoisted_39, [
                (__props.comment.embed.external.thumb)
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 0,
                      src: __props.comment.embed.external.thumb,
                      alt: __props.comment.embed.external.title,
                      class: "w-20 h-20 object-cover flex-shrink-0",
                      loading: "lazy"
                    }, null, 8, _hoisted_40))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_41, [
                  _createElementVNode("p", _hoisted_42, _toDisplayString(__props.comment.embed.external.title), 1),
                  _createElementVNode("p", _hoisted_43, _toDisplayString(__props.comment.embed.external.description), 1)
                ])
              ])
            ], 8, _hoisted_38))
          : _createCommentVNode("", true),
        (__props.comment.replies.length > 0)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              (__props.depth < maxDepth)
                ? (_openBlock(), _createElementBlock("div", _hoisted_44, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.comment.replies, (reply) => {
                      return (_openBlock(), _createBlock(_component_BlueskyComment, {
                        key: reply.uri,
                        comment: reply,
                        depth: __props.depth + 1
                      }, null, 8, ["comment", "depth"]))
                    }), 128))
                  ]))
                : (_openBlock(), _createElementBlock("a", {
                    key: 1,
                    href: getCommentUrl(__props.comment.replies[0]!),
                    target: "_blank",
                    rel: "noopener",
                    class: "text-sm text-muted hover:underline mt-2 block rounded f-ring"
                  }, _toDisplayString(__props.comment.replies.length) + " more " + _toDisplayString(__props.comment.replies.length === 1 ? 'reply' : 'replies') + "... ", 9, _hoisted_45))
            ], 64))
          : _createCommentVNode("", true)
      ]))
}
}

})